import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Book, Calendar, Award, Code, CheckCircle, Users, Image, Folder, Mail, ArrowRight
} from "lucide-react";
import api from "../api";
import { toast } from "react-hot-toast";

const DashboardCard = ({ title, value, icon: Icon, color }) => (
  <div className={`overflow-hidden shadow-lg rounded-xl ${color} text-white transition-all duration-300 hover:shadow-xl hover:scale-105`}>
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium truncate opacity-80">{title}</p>
          <p className="mt-1 text-3xl font-bold">{value}</p>
        </div>
        <div className="p-3 bg-white bg-opacity-30 rounded-full">
          <Icon className="h-10 w-10" />
        </div>
      </div>
    </div>
  </div>
);

const QuickLink = ({ title, icon: Icon, path }) => (
  <Link
    to={path}
    className="flex items-center p-4 bg-white rounded-xl shadow-md hover:bg-gray-50 transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
  >
    <Icon className="h-6 w-6 text-indigo-600 mr-3" />
    <span className="text-gray-700 font-medium">{title}</span>
    <ArrowRight className="h-4 w-4 text-gray-400 ml-auto" />
  </Link>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalColleges: 0,
    totalEvents: 0,
    upcomingEvents: 0,
    totalHackathons: 0,
    totalAchievements: 0,
    totalProjects: 0,
    ongoingProjects: 0,
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const statsResponse = await api.get("/stats/dashboard");
        setStats(statsResponse.data.data);
      } catch (error) {
        toast.error("Failed to fetch dashboard data");
      }
    };

    fetchDashboardData();
  }, []);

  const statCards = [
    { title: "Total Colleges", value: stats.totalColleges, icon: Book, color: "bg-gradient-to-br from-blue-500 to-blue-600" },
    { title: "Total Events", value: stats.totalEvents, icon: Calendar, color: "bg-gradient-to-br from-green-500 to-green-600" },
    { title: "Upcoming Events", value: stats.upcomingEvents, icon: Calendar, color: "bg-gradient-to-br from-yellow-500 to-yellow-600" },
    { title: "Total Hackathons", value: stats.totalHackathons, icon: Code, color: "bg-gradient-to-br from-purple-500 to-purple-600" },
    { title: "Total Achievements", value: stats.totalAchievements, icon: Award, color: "bg-gradient-to-br from-pink-500 to-pink-600" },
    { title: "Total Projects", value: stats.totalProjects, icon: CheckCircle, color: "bg-gradient-to-br from-indigo-500 to-indigo-600" },
    { title: "Ongoing Projects", value: stats.ongoingProjects, icon: Code, color: "bg-gradient-to-br from-red-500 to-red-600" },
  ];

  const quickLinks = [
    { title: "Colleges", icon: Book, path: "/colleges" },
    { title: "News", icon: Book, path: "/news" },
    { title: "Events", icon: Calendar, path: "/events" },
    { title: "Hackathons", icon: Code, path: "/hackathons" },
    { title: "Achievements", icon: Award, path: "/achievements" },
    { title: "Gallery", icon: Image, path: "/gallery" },
    { title: "Coordinators", icon: Users, path: "/coordinators" },
    { title: "Projects", icon: Folder, path: "/projects" },
    { title: "Contacts", icon: Mail, path: "/contacts" },
  ];

  return (
    <div className="space-y-8">
      <h1 className="text-4xl font-bold text-gray-900">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {statCards.map((item) => (
          <DashboardCard key={item.title} {...item} />
        ))}
      </div>

      <div className="bg-white shadow-lg rounded-xl p-6">
        <h2 className="text-2xl font-semibold mb-4">Quick Links</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {quickLinks.map((link) => (
            <QuickLink key={link.title} {...link} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;